// assistantFlyUi.js — A FLY PAGE'S UI SECTIONS (Fly train 2; docs/FLY_MODE.md
// §3.6). Nika 2026-10-18: "workspace fly is more advanced - it has way more
// tools and components and full capacity of use workspace features - so fully
// integrate it".
//
// A page may READ with the assistant's own tools (server ai-boards
// flyReads.js): the page model plans the reads right after its head, the
// server runs them under the member's own grants while the page streams, and
// a `ui` section SHOWS one read's result — the SAME payload a chat answer-UI
// tool call carries:
//   { tool: 'render_cards', args: { cards } }            people / agent chips
//   { tool: 'render_answer_ui', args: { title, source, placement } }
//                                                        a contained DOMQL view
// It is drawn with the chat's OWN renderers, never a second one:
//   render_cards      the chat's card dispatcher (shared ChatCard), one per card
//   render_answer_ui  renderAnswerUi (canvas functions/answerUi.js — the audit
//                     and the containment gate every answer view passes,
//                     registered and NOT opened) mounted inline the way the
//                     assistant's thread mounts it (assistantBlockMountView)
// The stream: board.item (the section and its read) → fly.read (the read
// settled) → fly.ui (what it shows: `ui`, `empty` or `error`); board.done
// carries the same on spec.items (functions/assistantFly.js applyFlyFrame).
//
// Frank note: el.call serializes each export on its own (FA204) — every
// export here is self-contained; a sibling is reached through this.call.

// What a UI section is doing: 'reading' (its read is out, or its result is on
// its way), 'ready' (its result is in), 'empty' (the read found nothing),
// 'error' (the read did not come back, or the page ended before its result
// came) — null for any other row. `s` is the section's cell (functions/
// assistantFly.js assistantFlyCells: the row + `readState` + `settled`).
export const assistantFlyUiState = function assistantFlyUiState(s) {
  if (!s || s.kind !== 'ui') return null
  if (s.error) return 'error'
  if (s.empty === true) return 'empty'
  if (s.ui) return 'ready'
  // The page is done and this section never heard back: the server's own
  // verdict for such a section is a timeout (AiBoardService.runFly).
  if (s.settled === true) return 'error'
  return 'reading'
}

// The ONE line a UI section says in place of its result: what it is reading
// ("Reading Projects…") until its read lands, that the read found nothing, or
// why it did not come back — one sentence per code (the server's codes:
// AiBoardService READ_ERROR_STATUS, plus `invalid_ui`). '' while it shows its
// result, and in the moment between its read landing and its result arriving.
export const assistantFlyUiNote = function assistantFlyUiNote(s) {
  const state = this.call('assistantFlyUiState', s)
  if (!state || state === 'ready') return ''
  const say = (key) => String(this.call('polyglot', key) || '')
  if (state === 'reading') {
    if (s.readState === 'ok') return ''
    return say('workspace.assistantFly.ui.reading').replace('{what}', String(s.title || ''))
  }
  if (state === 'empty') return say('workspace.assistantFly.ui.empty')
  const code = s.error && typeof s.error.code === 'string' ? s.error.code : 'timeout'
  // Literal keys (docs/I18N-CONVENTION.md: a key is never built).
  const KEYS = {
    timeout: 'workspace.assistantFly.ui.timeout',
    aborted: 'workspace.assistantFly.ui.aborted',
    role_required: 'workspace.assistantFly.ui.roleRequired',
    tool_unavailable: 'workspace.assistantFly.ui.toolUnavailable',
    not_found: 'workspace.assistantFly.ui.notFound',
    source_failed: 'workspace.assistantFly.ui.sourceFailed',
    read_failed: 'workspace.assistantFly.ui.failed',
    invalid_ui: 'workspace.assistantFly.ui.invalid'
  }
  return say(Object.prototype.hasOwnProperty.call(KEYS, code) ? KEYS[code] : KEYS.read_failed)
}

// Mount a UI section's result into its card body (`el` = the card's CardBody)
// with the chat's own renderers — once per result (the card re-renders on
// every frame of its page). A result the chat's gate refuses (renderAnswerUi:
// the audit, the containment parse and its style lint) or a payload it cannot
// read says ONE quiet line instead, never an empty frame, and that line is
// `empty`: the cell takes one row (functions/assistantBoard.js
// assistantBoardCardEmpty). → true when the result is drawn.
export const assistantFlyUiMount = function assistantFlyUiMount(el, s) {
  const host = el || this
  if (!host || typeof host.set !== 'function' || !s || s.kind !== 'ui') return false
  const ui = s.ui && typeof s.ui === 'object' ? s.ui : null
  if (!ui) return false
  const sig = 'ui/' + String(s.id || '') + '/' + this.call('boardSpecHash', ui)
  if (host.__flyUiSig === sig) return host.__flyUiDrawn === true
  host.__flyUiSig = sig
  host.__flyUiDrawn = false
  const args = ui.args && typeof ui.args === 'object' ? ui.args : {}
  const quiet = (why) => {
    console.warn('[assistantFly] a ui section was not drawn:', s.tool || '', why || '')
    try {
      host.set({
        tag: 'p',
        margin: '0',
        fontSize: 'Z',
        color: 'caption',
        empty: true,
        text: String(this.call('polyglot', 'workspace.assistantFly.ui.invalid'))
      })
    } catch (_) {
      /* the host is gone */
    }
    this.call('assistantBoardBodyMounted', host)
    return false
  }
  if (ui.tool === 'render_cards') {
    // The chat's own cards (the render_cards tool's `cards`, the shapes
    // ChatCard draws — people, tickets, thumbnails …), each a card state.
    const cards = (Array.isArray(args.cards) ? args.cards : [])
      .filter(
        (c) => c && typeof c === 'object' && typeof c.type === 'string' && Array.isArray(c.items)
      )
      .slice(0, 4)
      .map((c, i) => ({
        key: 'card' + i,
        type: c.type,
        items: c.items.filter((it) => it && typeof it === 'object').slice(0, 20)
      }))
      .filter((c) => c.items.length)
    if (!cards.length) return quiet('no cards')
    try {
      // The chat message's own card list (shared ChatMessage `Cards`): the
      // cards as state, one ChatCard each.
      host.set({
        tag: 'div',
        flow: 'y',
        gap: 'Z',
        minWidth: '0',
        state: { cards },
        childExtends: 'ChatCard',
        childrenAs: 'state',
        children: (el, st) => (st && st.cards) || []
      })
    } catch (err) {
      return quiet(err && err.message)
    }
    host.__flyUiDrawn = true
    this.call('assistantBoardBodyMounted', host)
    return true
  }
  if (ui.tool === 'render_answer_ui') {
    // The chat's answer view: audited, parsed, registered — not opened (the
    // thread's inline path) — then mounted here as the thread mounts it.
    let res = null
    try {
      res = this.call('renderAnswerUi', {
        title: typeof args.title === 'string' ? args.title : String(s.title || ''),
        source: typeof args.source === 'string' ? args.source : '',
        placement: args.placement === 'split' ? 'split' : 'inline',
        open: false
      })
    } catch (err) {
      res = { ok: false, error: err && err.message }
    }
    if (!res || res.ok !== true || typeof res.opened !== 'string') {
      return quiet(res && (res.error || (res.violations || []).join(', ')))
    }
    const shown = this.call('assistantBlockMountView', host, {
      type: 'view',
      route: res.opened,
      root: s.root
    })
    if (!shown) return quiet('the view is gone')
    host.__flyUiDrawn = true
    this.call('assistantBoardBodyMounted', host)
    return true
  }
  return quiet('unknown tool')
}
